/**
 * Global toast stack — top-right (never move: clears the Setup dock and
 * leaves the Account / app content alone). Soft light cards, no accent rail.
 */
import React from 'react';
import { Check, X as XIcon, AlertTriangle, Info, X } from 'lucide-react';
import { useToastStore, Toast, ToastType } from '../../stores/toastStore';

/** Single source of truth for toast chrome (soft tint + icon). */
export const TOAST_VARIANTS: Record<
  ToastType,
  { iconWrap: string; icon: React.ReactNode }
> = {
  success: {
    iconWrap: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100',
    icon: <Check className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden />,
  },
  error: {
    iconWrap: 'bg-rose-50 text-rose-700 ring-1 ring-rose-100',
    icon: <XIcon className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden />,
  },
  warning: {
    iconWrap: 'bg-amber-50 text-amber-800 ring-1 ring-amber-100',
    icon: <AlertTriangle className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden />,
  },
  info: {
    iconWrap: 'bg-sky-50 text-sky-700 ring-1 ring-sky-100',
    icon: <Info className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden />,
  },
};

/** Shared layout — light elevated card (Linear / Vercel style). */
export const TOAST_PANEL_BASE =
  'pointer-events-auto flex w-full max-w-sm items-center gap-3 rounded-2xl border border-gray-200/80 bg-white/95 px-3.5 py-3 text-gray-900 shadow-[0_8px_30px_rgba(15,23,42,0.08),0_2px_8px_rgba(15,23,42,0.04)] backdrop-blur-md bbx-toast-in';

// The entrance is a CSS animation INTO the resting, on-screen state
// (`bbx-toast-in`, index.css). Resting state stays on-screen and opaque —
// never mount at translate-x-full/opacity-0 (late frames parked toasts off-screen).
const ToastItem: React.FC<{ toast: Toast; onClose: (id: string) => void }> = ({ toast, onClose }) => {
  const variant = TOAST_VARIANTS[toast.type];

  return (
    <div className={TOAST_PANEL_BASE} role="alert">
      <span
        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-xl ${variant.iconWrap}`}
      >
        {variant.icon}
      </span>
      <p className="min-w-0 flex-1 text-sm font-medium leading-snug text-gray-800">
        {toast.message}
      </p>
      {toast.action && (
        <button
          type="button"
          onClick={() => {
            toast.action?.onClick();
            onClose(toast.id);
          }}
          className="shrink-0 rounded-lg bg-gray-900 px-2.5 py-1 text-xs font-semibold text-white transition-colors hover:bg-gray-800"
        >
          {toast.action.label}
        </button>
      )}
      <button
        type="button"
        onClick={() => onClose(toast.id)}
        className="shrink-0 rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
        aria-label="Close notification"
      >
        <X className="h-3.5 w-3.5" aria-hidden />
      </button>
    </div>
  );
};

export const ToastContainer: React.FC = () => {
  const toasts = useToastStore((state) => state.toasts);
  const removeToast = useToastStore((state) => state.removeToast);

  if (toasts.length === 0) return null;

  // Top-right clears the Setup checklist pill (fixed bottom-right, z-[61]).
  // `left-4` below `sm` keeps a gutter on narrow screens; each toast re-enables
  // pointer events so the empty strip never swallows presses meant for the page.
  return (
    <div className="pointer-events-none fixed top-4 right-4 left-4 z-50 flex flex-col items-end gap-2 sm:left-auto">
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onClose={removeToast} />
      ))}
    </div>
  );
};
